import { useGetQuizPublic } from '@repo/api/quiz/useGetQuizPublic';
import { getBestQuizResponse } from '@repo/lib/quiz/getBestQuizResponse';
import { getQuizAttemptTimeUnit } from '@repo/lib/quiz/getQuizAttemptTimeUnit';
import { format, formatDistanceStrict } from 'date-fns';
import { CircleSlashIcon, EyeIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import QuizDetailHeader from '@/components/features/quiz/header/QuizDetailHeader';
import QuizPercentageResult from '@/components/features/quiz/results/QuizPercentageResult';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQuizParams } from '@/hooks/quiz';

export default function QuizDetailPage() {
  const { id } = useQuizParams();
  const { data, isPending, isError, errorMessage } = useGetQuizPublic(id);
  const navigate = useNavigate();

  const bestQuizResp = getBestQuizResponse(data?.quizresps);
  const hasAttempts = useMemo<boolean>(() => (data?.quizresps.length ?? 0) > 0, [data?.quizresps.length]);
  const hasPendingVerification = useMemo<boolean>(() => {
    if (!hasAttempts) {
      return false;
    }

    const hasScoredAttempt: boolean = data?.quizresps.some((attempt) => attempt.score != null) ?? false;

    return !hasScoredAttempt;
  }, [data?.quizresps, hasAttempts]);

  if (isPending) {
    return (
      <div className="base-direction-col">
        {/* Header skeleton */}
        <div className="border-border-primary flex w-full flex-col gap-2 rounded-2xl border p-4">
          <Skeleton className="h-4 w-16 rounded-sm" />
          <Skeleton className="my-1 h-7 w-32 rounded-sm" />
          <div className="mt-2 flex flex-wrap gap-2">
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </div>

        {/* Main content layout */}
        <div className="flex h-full w-full flex-col gap-4 lg:flex-row">
          {/* Results panel skeleton */}
          <div className="bg-background w-full rounded-2xl border lg:w-xs lg:min-w-xs">
            <div className="w-full border-b p-4">
              <Skeleton className="h-5 w-16 rounded-sm" />
            </div>
            <div className="space-y-4 p-4">
              {/* Percentage circle skeleton */}
              <div className="flex items-center justify-center py-4">
                <Skeleton className="h-28 w-28 rounded-full" />
              </div>
              {/* Stats skeleton */}
              <div className="space-y-3">
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-full rounded-md" />
              </div>
            </div>
          </div>

          {/* Table skeleton */}
          <div className="w-full grow">
            <div className="bg-background relative h-full w-full overflow-x-auto rounded-2xl border">
              <div className="border-border-primary border-b">
                <div className="flex gap-4 p-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="hidden h-4 w-16 sm:block" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="hidden h-4 w-20 md:block" />
                  <Skeleton className="ml-auto h-4 w-12" />
                </div>
              </div>
              {/* Table rows skeleton */}
              <div className="space-y-4 p-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 border-b pb-4">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="hidden h-4 w-12 sm:block" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="hidden h-4 w-20 md:block" />
                    <Skeleton className="ml-auto h-8 w-24 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className={'z-0'} />
          <FeaturedIcon size={'lg'} className={'z-10'}>
            <CircleSlashIcon />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="base-direction-col">
      <QuizDetailHeader attempt={bestQuizResp} quiz={data} />
      <div className="flex h-full w-full flex-col gap-4 lg:flex-row">
        <div className="bg-background w-full rounded-2xl border lg:w-xs lg:min-w-xs">
          <div className="w-full border-b p-4">
            <h5 className="text-base font-semibold">Wyniki</h5>
          </div>
          <div className="p-4">
            {hasAttempts ? (
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="max-w-52">
                  <QuizPercentageResult attemptScore={bestQuizResp?.score} maxScore={data.points} />
                </div>

                {hasPendingVerification && (
                  <Alert>
                    <AlertTitle>{bestQuizResp?.score != null ? 'Wynik nie jest ostateczny' : 'Wymagana weryfikacja'}</AlertTitle>
                    <AlertDescription>
                      {bestQuizResp?.score != null
                        ? 'Część odpowiedzi nadal czeka na ręczną ocenę. Wynik może się jeszcze zmienić.'
                        : 'Niektóre odpowiedzi wymagają ręcznej oceny przez nauczyciela. Otrzymasz powiadomienie, gdy pełny wynik będzie dostępny.'}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="max-w-52">
                  <QuizPercentageResult attemptScore={undefined} maxScore={data.points} />
                </div>
                <Alert variant={'warning'}>
                  <AlertDescription>Brak ukończonego podejścia. Wyniki zostaną pokazane po ukończeniu quizu.</AlertDescription>
                </Alert>
              </div>
            )}
          </div>
        </div>
        <div className="w-full grow">
          <div className="bg-background relative h-full w-full overflow-x-auto rounded-2xl border">
            <Table className="grow">
              <TableHeader className="border-border-primary border-b">
                <TableRow>
                  <TableHead className="">Punkty</TableHead>
                  <TableHead className="hidden sm:table-cell">Wynik</TableHead>
                  <TableHead className="">Czas rozpoczęcia</TableHead>
                  <TableHead className="hidden md:table-cell">Czas podejścia</TableHead>
                  <TableHead> </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.quizresps.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      Brak ukończonych podejść
                    </TableCell>
                  </TableRow>
                )}
                {data.quizresps.map((attempt) => {
                  const percentage: number = attempt.score != null ? (attempt.score / data.points) * 100 : NaN;

                  return (
                    <TableRow key={attempt.id}>
                      <TableCell>
                        {attempt.score == null ? '-' : attempt.score} / {data.points}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">{attempt.score == null ? 'W trakcie oceny' : `${percentage.toPrecision(3)}%`}</TableCell>
                      <TableCell>{format(attempt.createdAt, 'dd.MM.yyyy HH:mm')}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {attempt.completedAt
                          ? formatDistanceStrict(attempt.completedAt, attempt.createdAt, {
                              unit: getQuizAttemptTimeUnit(attempt),
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button variant={'outline'} size={'sm'} onClick={() => navigate(`/quizzes/${data.id}/attempt/${attempt.id}/result`)}>
                            <EyeIcon />
                            Podgląd
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
